import { useQuery } from '@tanstack/react-query';

import { getEligibleParentFlightOrders } from '~/components/Common/CustomOffers/parentOrder/parentFlightOrderUtils';

import { ITEM_TYPE_CUSTOM_OFFER, ITEM_TYPE_FLIGHT } from '~/consts/order';

import { getPurchases } from '~/services/OrdersService';

const PARENT_ORDERS_PAGE_SIZE = 100;

const PARENT_ORDER_ITEM_TYPES = [ITEM_TYPE_FLIGHT, ITEM_TYPE_CUSTOM_OFFER];

export const parentFlightOrderQueryKeys = {
  all: ['parentFlightOrders'] as const,
  list: (brand: string, customerId: string) => [...parentFlightOrderQueryKeys.all, brand, customerId] as const,
};

type FlightOrdersQuery = {
  brand: string;
  customerId: string;
  signal?: AbortSignal;
};

function fetchFlightOrdersPage({ brand, customerId, signal }: FlightOrdersQuery, page: number) {
  return getPurchases({
    page,
    per_page: PARENT_ORDERS_PAGE_SIZE,
    brand,
    customer_id: customerId,
    item_type: PARENT_ORDER_ITEM_TYPES,
    signal,
  });
}

async function fetchCustomerFlightOrders(query: FlightOrdersQuery): Promise<Array<App.Order>> {
  const firstPage = await fetchFlightOrdersPage(query, 1);
  const pageCount = Math.ceil(Number(firstPage.total ?? 0) / PARENT_ORDERS_PAGE_SIZE);
  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) => fetchFlightOrdersPage(query, index + 2)),
  );

  return [firstPage, ...remainingPages].flatMap((page) => (page.result ?? []) as Array<App.Order>);
}

/** Completed orders holding a flight the customer can still raise a post-purchase offer against. */
export function useGetParentFlightOrders(brand: string, customerId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: parentFlightOrderQueryKeys.list(brand, customerId ?? ''),
    queryFn: async ({ signal }) =>
      getEligibleParentFlightOrders(await fetchCustomerFlightOrders({ brand, customerId, signal })),
    enabled: enabled && !!brand && !!customerId,
  });
}
